import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { apiClient } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { Partner, PartnerType } from "@tilt/admin-sdk";

/**
 * Reusable partner dropdown.
 *
 * Every form that needs a partner selector (parent-partner on Partners,
 * grant-role on Users, credential owner on ProviderCredentials, …)
 * uses THIS component. Keeps the name/path render + sort + scope
 * filter consistent, and stops admins from having to paste UUIDs.
 *
 * Props:
 *   - value / onChange   - standard controlled-form shape; value is
 *                          the partner id (or "" for "none selected").
 *   - partnerType         - optional filter: one specific partner type,
 *                          or an array. Undefined = all types.
 *   - includeDeleted     - show soft-deleted partners too. Default false.
 *   - showType           - append "(Type)" to each option label. Default
 *                          true - helps distinguish white_label from
 *                          customer_org when picking a parent.
 *   - emptyLabel         - text shown when no value is selected yet.
 *   - required           - passthrough to <select>.
 *   - disabled           - manually disable (overrides auto-disable
 *                          while the list is loading).
 *   - className          - merged into the base select classes.
 *   - id / name          - passthrough for form association.
 */
export interface PartnerPickerProps {
  value: string;
  onChange: (id: string) => void;
  partnerType?: PartnerType | PartnerType[];
  includeDeleted?: boolean;
  showType?: boolean;
  emptyLabel?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
  /** Show the platform partner row in the dropdown.
   *
   *  Platform is the abstract ROOT of the tenancy tree, not a real
   *  business unit. It only belongs in places that explicitly
   *  manage the root (Quick onboard's "parent partner" for the
   *  first sub-partner, the Partners list when seeded by
   *  super_admin). Surfacing it in invite/grant flows confused
   *  operators - they'd see "Platform (Platform)" alongside real
   *  partners and not know what to pick. Default false.
   */
  includePlatform?: boolean;
}

export function PartnerPicker(props: PartnerPickerProps): JSX.Element {
  // Query key segments the partnerType + includeDeleted so a page that
  // renders two pickers with different filters doesn't share a cache
  // slot that silently shows wrong rows.
  const typeKey = Array.isArray(props.partnerType)
    ? [...props.partnerType].sort().join(",")
    : (props.partnerType ?? "all");
  const query = useQuery({
    queryKey: ["partners", "picker", typeKey, props.includeDeleted ?? false],
    queryFn: () =>
      apiClient.listPartners({
        ...(props.includeDeleted ? { include_deleted: true } : {}),
        // listPartners only accepts a single type; when the caller passed
        // an array, fetch all and filter client-side.
        ...(typeof props.partnerType === "string" ? { partner_type: props.partnerType } : {}),
      }),
    retry: false,
  });

  const choices = useMemo(() => {
    const rows = (query.data?.partners ?? []).filter((t) => props.includeDeleted || !t.is_deleted);
    const typed = Array.isArray(props.partnerType)
      ? rows.filter((t) => (props.partnerType as PartnerType[]).includes(t.partner_type))
      : rows;
    // Drop the synthetic platform row unless the consumer explicitly
    // opts in. See ``includePlatform`` doc for the rationale.
    const filtered = props.includePlatform
      ? typed
      : typed.filter((t) => t.partner_type !== "platform");
    return filtered.slice().sort((a, b) => a.path.localeCompare(b.path));
  }, [query.data, props.partnerType, props.includeDeleted, props.includePlatform]);

  const showType = props.showType ?? true;
  const emptyLabel = props.emptyLabel ?? "Select a partner…";

  return (
    <select
      id={props.id}
      name={props.name}
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
      required={props.required}
      disabled={props.disabled || query.isLoading}
      className={cn(
        "w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-60",
        props.className,
      )}
    >
      <option value="">
        {query.isLoading
          ? "Loading partners…"
          : choices.length === 0
            ? "No partners in scope"
            : emptyLabel}
      </option>
      {choices.map((t) => (
        <option key={t.id} value={t.id}>
          {labelFor(t, showType)}
        </option>
      ))}
    </select>
  );
}

function labelFor(t: Partner, showType: boolean): string {
  // Users address partners by name; `path` is an internal slug chain
  // ("platform.finterra") that doesn't belong in a picker option.
  // Fall back to path only if `name` is missing - unusual but
  // conceivable on a half-migrated row.
  const head = t.name || t.path;
  if (!showType) return head;
  // Legacy rows may still carry a non-live partner_type - normalise
  // the display label so the picker doesn't render "(undefined)".
  const typeLabel = t.partner_type === "platform" ? "Platform" : "Partner";
  return `${head} (${typeLabel})`;
}
